// Country code + national number handling for the optional candidate mobile
// captured when an assessment is created or candidates are added to an existing
// one.
//
// The country code is a SEPARATE field so recruiters never have to guess whether
// it belongs inside the number. Before that split, a single field had to infer the
// prefix, and stripping a leading `91` unconditionally mangled genuine 91-series
// Indian mobiles (9123456701 → the 8-digit 23456701).
//
// Recruiters still paste full international numbers into the number box out of
// habit, so the number field also drops a country code that is merely REDUNDANT —
// i.e. only when removing it leaves a valid national length for the selected
// country. That keeps 9123456701 intact while accepting a pasted +91 9123456701.

// Dial codes the platform serves, India first (the overwhelming default).
// `digits` is the exact national-number length(s); used for validation and to
// decide whether a pasted prefix is redundant.
// Kept as a static list on purpose: /search/countries would make an optional
// field depend on the search service, and a country code is not worth a fetch.
export const COUNTRY_DIAL_CODES = [
  { code: '+91', label: '🇮🇳 +91', country: 'India', digits: [10] },
  { code: '+1', label: '🇺🇸 +1', country: 'USA / Canada', digits: [10] },
  { code: '+44', label: '🇬🇧 +44', country: 'United Kingdom', digits: [10] },
  { code: '+971', label: '🇦🇪 +971', country: 'UAE', digits: [9] },
  { code: '+966', label: '🇸🇦 +966', country: 'Saudi Arabia', digits: [9] },
  { code: '+974', label: '🇶🇦 +974', country: 'Qatar', digits: [8] },
  { code: '+965', label: '🇰🇼 +965', country: 'Kuwait', digits: [8] },
  { code: '+968', label: '🇴🇲 +968', country: 'Oman', digits: [8] },
  { code: '+973', label: '🇧🇭 +973', country: 'Bahrain', digits: [8] },
  { code: '+65', label: '🇸🇬 +65', country: 'Singapore', digits: [8] },
  { code: '+60', label: '🇲🇾 +60', country: 'Malaysia', digits: [9, 10] },
  { code: '+61', label: '🇦🇺 +61', country: 'Australia', digits: [9] },
  { code: '+64', label: '🇳🇿 +64', country: 'New Zealand', digits: [8, 9] },
  { code: '+49', label: '🇩🇪 +49', country: 'Germany', digits: [10, 11] },
  { code: '+33', label: '🇫🇷 +33', country: 'France', digits: [9] },
  { code: '+31', label: '🇳🇱 +31', country: 'Netherlands', digits: [9] },
  { code: '+41', label: '🇨🇭 +41', country: 'Switzerland', digits: [9] },
  { code: '+353', label: '🇮🇪 +353', country: 'Ireland', digits: [9] },
  { code: '+81', label: '🇯🇵 +81', country: 'Japan', digits: [10] },
  { code: '+82', label: '🇰🇷 +82', country: 'South Korea', digits: [9, 10] },
  { code: '+86', label: '🇨🇳 +86', country: 'China', digits: [11] },
  { code: '+27', label: '🇿🇦 +27', country: 'South Africa', digits: [9] },
  { code: '+94', label: '🇱🇰 +94', country: 'Sri Lanka', digits: [9] },
  { code: '+977', label: '🇳🇵 +977', country: 'Nepal', digits: [10] },
  { code: '+880', label: '🇧🇩 +880', country: 'Bangladesh', digits: [10] },
]

export const DEFAULT_COUNTRY_CODE = '+91'

// Longest dial code first, so `+1` never shadows `+91`/`+971` when matching a
// pasted prefix.
const DIAL_CODES_BY_LENGTH = [...COUNTRY_DIAL_CODES].sort(
  (a, b) => b.code.length - a.code.length
)

const findDialCode = code =>
  COUNTRY_DIAL_CODES.find(entry => entry.code === code) || null

/** Options for an antd Select. */
export const countryCodeOptions = COUNTRY_DIAL_CODES.map(
  ({ code, label, country }) => ({
    value: code,
    label,
    // Matched by Select's optionFilterProp so typing "india" or "91" both work.
    searchText: `${code} ${country}`,
  })
)

/**
 * Reduces a typed/pasted number to its national digits for the selected country.
 * @param {string} countryCode e.g. '+91'
 * @param {string} rawNumber as typed (may carry +, spaces, hyphens, 0, or a
 *   redundant copy of the country code)
 * @returns {string|null} national number, or null when it isn't a valid length
 */
export const normalizeNationalNumber = (countryCode, rawNumber) => {
  const entry = findDialCode(countryCode)
  if (!entry) return null

  let digits = String(rawNumber || '').replace(/\D/g, '')
  const bare = entry.code.replace('+', '')

  // Drop a redundant copy of the selected country code, but ONLY when what
  // remains is a valid national length — otherwise a 91-series Indian mobile
  // would lose its first two digits.
  if (
    digits.length > bare.length &&
    digits.startsWith(bare) &&
    entry.digits.includes(digits.length - bare.length)
  ) {
    digits = digits.slice(bare.length)
  }

  // National trunk prefix ('0'), same redundancy rule.
  if (digits.startsWith('0') && entry.digits.includes(digits.length - 1)) {
    digits = digits.slice(1)
  }

  return entry.digits.includes(digits.length) ? digits : null
}

/**
 * Full parse of a country code + number pair.
 * @returns {{countryCode: string, nationalNumber: string, e164: string}|null}
 */
export const parseCandidateMobile = (countryCode, rawNumber) => {
  const resolvedCode = findDialCode(countryCode) ? countryCode : DEFAULT_COUNTRY_CODE
  const nationalNumber = normalizeNationalNumber(resolvedCode, rawNumber)
  if (!nationalNumber) return null
  return {
    countryCode: resolvedCode,
    nationalNumber,
    e164: `${resolvedCode}${nationalNumber}`,
  }
}

/**
 * Splits a full number that already carries its country code (a bulk-upload cell
 * like "+971 501234567", or a legacy single-field value) into a code + national
 * pair. Falls back to `fallbackCode` when no code is present — which is the
 * common case, since most sheets just list bare local numbers.
 * @returns {{countryCode: string, nationalNumber: string, e164: string}|null}
 */
export const splitFullMobile = (rawNumber, fallbackCode = DEFAULT_COUNTRY_CODE) => {
  const raw = String(rawNumber || '').trim()
  if (!raw) return null
  const digits = raw.replace(/\D/g, '')
  if (!digits) return null

  // An explicit '+' (or a 00 IDD prefix) means the country code is really there,
  // so trust it and match the longest one that leaves a valid national length.
  const isExplicit = raw.startsWith('+') || digits.startsWith('00')
  const body = digits.replace(/^00/, '')

  for (const entry of DIAL_CODES_BY_LENGTH) {
    const bare = entry.code.replace('+', '')
    if (!body.startsWith(bare)) continue
    if (!entry.digits.includes(body.length - bare.length)) continue
    // Without an explicit '+', only treat a prefix as a country code when the
    // number can't already stand alone as a national number for the fallback —
    // otherwise a bare Indian 9123456701 would be read as +91 23456701.
    if (!isExplicit && normalizeNationalNumber(fallbackCode, body)) break
    return {
      countryCode: entry.code,
      nationalNumber: body.slice(bare.length),
      e164: `${entry.code}${body.slice(bare.length)}`,
    }
  }

  return parseCandidateMobile(fallbackCode, digits)
}

/**
 * Coerces a spreadsheet country-code cell to a supported dial code. Handles the
 * shapes Excel actually produces: "+91", "91", 91 as a number, "0091".
 * Unrecognised or empty → +91, so sheets without the column keep working.
 */
export const normalizeCountryCodeCell = cell => {
  const digits = String(cell ?? '')
    .replace(/\D/g, '')
    .replace(/^00/, '')
  if (!digits) return DEFAULT_COUNTRY_CODE
  return findDialCode(`+${digits}`) ? `+${digits}` : DEFAULT_COUNTRY_CODE
}

/** Display form for the candidates table: "+91 9123456701". */
export const formatMobile = (countryCode, nationalNumber) =>
  nationalNumber ? `${countryCode || DEFAULT_COUNTRY_CODE} ${nationalNumber}` : ''

/** Placeholder/length hint for the number input of the selected country. */
export const nationalNumberLength = countryCode => {
  const entry = findDialCode(countryCode)
  return entry ? Math.max(...entry.digits) : 10
}
